import { catchError, defer, filter, map, Observable, of, shareReplay, startWith, switchMap } from 'rxjs';

/** The Firebase uid a value was loaded for, or null for the signed-out state. */
export type SessionUid = string | null;

/**
 * A value loaded for one sign-in, tagged with whose sign-in it was.
 *
 * `pending` is cached alongside the settled states on purpose. A shared stream that
 * only ever caches settled values keeps replaying the previous session's value while
 * the next one loads, and that is exactly how sign-in broke: the signed-out `null` was
 * still being replayed when the route guards asked for the agent who had just signed in.
 */
export type SessionValue<T> =
  | { readonly uid: SessionUid; readonly status: 'pending' }
  | { readonly uid: SessionUid; readonly status: 'ready'; readonly value: T }
  | { readonly uid: SessionUid; readonly status: 'failed'; readonly error: unknown };

type SettledSessionValue<T> = Exclude<SessionValue<T>, { status: 'pending' }>;

export const sessionPending = <T>(uid: SessionUid): SessionValue<T> => ({ uid, status: 'pending' });
export const sessionReady = <T>(uid: SessionUid, value: T): SessionValue<T> => ({ uid, status: 'ready', value });
export const sessionFailed = <T>(uid: SessionUid, error: unknown): SessionValue<T> => ({
  uid,
  status: 'failed',
  error,
});

/**
 * A failure becomes a value instead of an error, so it ends only this session's load and
 * leaves the shared stream running for the next sign-in.
 */
const settle = <T>(uid: SessionUid, load: () => Observable<T>): Observable<SessionValue<T>> =>
  defer(load).pipe(
    map(value => sessionReady(uid, value)),
    catchError(error => of(sessionFailed<T>(uid, error))),
    startWith(sessionPending<T>(uid)),
  );

/**
 * Loads a value for each signed-in user and shares it.
 *
 * `switchMap`, so a sign-out drops the previous user's load (and any live watch inside
 * it) instead of letting it keep emitting into the next session.
 */
export const loadPerSession = <U extends { uid: string }, T>(
  user$: Observable<U | null>,
  load: (user: U) => Observable<T>,
  signedOutValue: T,
): Observable<SessionValue<T>> =>
  user$.pipe(
    switchMap(user => (user ? settle(user.uid, () => load(user)) : of(sessionReady(null, signedOutValue)))),
    shareReplay(1),
  );

/**
 * What a reader sees: settled values for the session as it stands at the moment of
 * emission, and nothing else.
 *
 * The uid is compared at emission time, not when the value was cached, which covers the
 * gap between Firebase changing `currentUser` and the shared stream hearing about it.
 * Until the two agree the reader simply waits.
 *
 * A failed load is rethrown, so a reader cannot mistake "could not load" for "there is
 * nothing to load".
 */
export const currentSessionValue = <T>(
  source$: Observable<SessionValue<T>>,
  currentUid: () => SessionUid,
): Observable<T> =>
  source$.pipe(
    filter((entry): entry is SettledSessionValue<T> => entry.status !== 'pending' && entry.uid === currentUid()),
    map(entry => {
      if (entry.status === 'failed') {
        throw entry.error;
      }

      return entry.value;
    }),
  );
